import React from 'react';
import { View, TouchableOpacity } from 'react-native';
import { ActivityCard, PlatformIcon, ORANGE_ACTION } from '@cubemeet/ui';
import { mergePayload, coalesceText, getTypeIcon, buildMetaPairs, deriveImageUrl } from './itineraryUtils';

interface Props {
  it: any;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onOpenPoi?: (payload: any) => void;
  dayDate?: string;
  onImageClick?: (images: any[], startIndex: number) => void;
  /** 是否在遊記上下文中渲染（不顯示時間，顯示地址/電話等） */
  isTravelPost?: boolean;
}

const ActivityAdapter: React.FC<Props> = ({ it, isExpanded = false, onOpenPoi, dayDate, onImageClick, isTravelPost = false }) => {
  const mergedPayload = mergePayload(it) || {};
  const title = coalesceText(it?.title, it?.card?.title, mergedPayload.title) || '未命名';
  // 優先使用 it.note（行程表副本的描述），再使用 it.description（前端更新後的）
  const description = coalesceText(it?.note, it?.description, mergedPayload.description, it?.card?.description);
  
  // Helper: safely extract a readable string from various shapes
  const extractString = (value: any): string | undefined => {
    if (value == null) return undefined;
    if (typeof value === 'string') return value.trim() || undefined;
    if (Array.isArray(value)) {
      const parts = value.map(v => extractString(v)).filter(Boolean) as string[];
      return parts.length ? parts.join(' ') : undefined;
    }
    if (typeof value === 'object') {
      const candidates = ['formatted_address', 'formattedAddress', 'address', 'display', 'label', 'name', 'title', 'short_name', 'description', 'locality'];
      for (const k of candidates) {
        if (value[k]) {
          const s = extractString(value[k]);
          if (s) return s;
        }
      }
      const values = Object.values(value).map(v => (typeof v === 'string' ? v : (typeof v === 'number' ? String(v) : ''))).filter(Boolean);
      if (values.length) return values.join(' ');
      return undefined;
    }
    return String(value).trim() || undefined;
  };
  
  const venue = extractString(coalesceText(it?.card?.location, mergedPayload.location, mergedPayload.address));
  const startTime = mergedPayload.start_time || mergedPayload.startTime || mergedPayload.from || undefined;
  const endTime = mergedPayload.end_time || mergedPayload.endTime || mergedPayload.to || undefined;
  let imageUrl = deriveImageUrl(it, mergedPayload);
  // If this item is actually an external-type payload (external link shared into itinerary),
  // prefer explicit external images (custom_payload or base_payload) and DO NOT fallback to
  // gallery/notes which are user uploads.
  const isExternalPayload = (mergedPayload && (mergedPayload.type === 'external' || mergedPayload.card_type === 'external')) ||
    (it && it.card && it.card.card_type === 'external');
  if (isExternalPayload) {
    const pickDirectString = (...vals: any[]) => {
      for (const v of vals) {
        if (typeof v === 'string') {
          const t = v.trim();
          if (t) return t;
        }
      }
      return undefined;
    };
    const customPayloadImg = pickDirectString(
      it?.custom_payload && typeof it.custom_payload.imageUrl === 'string' && it.custom_payload.imageUrl,
      it?.custom_payload && typeof it.custom_payload.image_url === 'string' && it.custom_payload.image_url,
      it?.custom_payload && typeof it.custom_payload.image === 'string' && it.custom_payload.image,
    );
    const basePayloadImg = pickDirectString(
      it?.card && it.card.base_payload && typeof it.card.base_payload.imageUrl === 'string' && it.card.base_payload.imageUrl,
      mergedPayload && typeof mergedPayload.imageUrl === 'string' && mergedPayload.imageUrl,
      mergedPayload && typeof mergedPayload.image_url === 'string' && mergedPayload.image_url,
    );
    // prefer custom payload -> base payload -> fall back to deriveImageUrl (defensive)
    const resolved = customPayloadImg || basePayloadImg || deriveImageUrl(it, {});
    imageUrl = resolved;
  }

  const metaPairs = buildMetaPairs(it, mergedPayload);
  const transportNote = (() => {
    const entry = metaPairs.find((pair) => {
      if (!pair || !pair.label) return false;
      const label = String(pair.label).trim();
      return label === '交通方式' || label === '交通';
    });
    if (!entry || !entry.value) return undefined;
    return String(entry.value).trim() || undefined;
  })();

  // 提取地址和電話（用於遊記模式）
  const address = extractString(coalesceText(
    it?.card?.address,
    it?.card?.base_payload?.address,
    mergedPayload.address,
    it?.card?.location?.address,
    mergedPayload.location?.address
  ));
  
  const phone = extractString(coalesceText(
    it?.card?.phone,
    it?.card?.base_payload?.phone,
    mergedPayload.phone,
    it?.card?.tel,
    it?.card?.base_payload?.tel,
    mergedPayload.tel,
    it?.card?.telephone,
    mergedPayload.telephone
  ));

  const payloadForModal = {
    title,
    imageUrl,
    address: mergedPayload.address || it?.card?.address,
    description,
    metaPairs,
    card: it?.card,
    id: it?.card?.id || it?.meta?.cardId || it?.cardId,
  };

  const handlePress = () => {
    if (typeof onOpenPoi === 'function') {
      onOpenPoi(payloadForModal);
      return;
    }
    if (typeof onToggleExpand === 'function') onToggleExpand?.();
  };

  const card = (
    <ActivityCard
      title={title}
      note={description}
      venue={venue}
      startTime={isTravelPost ? undefined : startTime}
      endTime={isTravelPost ? undefined : endTime}
      actionIcon={<PlatformIcon name={getTypeIcon('activity')} size={16} color={ORANGE_ACTION} />}
      transportNote={isTravelPost ? undefined : transportNote}
      imageUrl={imageUrl}
      address={isTravelPost ? address : undefined}
      phone={isTravelPost ? phone : undefined}
    />
  );

  if (typeof onOpenPoi === 'function') {
    return (
      <TouchableOpacity activeOpacity={0.8} onPress={handlePress}>
        {card}
      </TouchableOpacity>
    );
  }

  return card;
};

export default ActivityAdapter;
